import { useEffect, useState } from 'react'
import { AlertTriangle } from 'lucide-react'

const NOTICE_TONE_STYLES = {
  brand: {
    containerClassName: 'border border-[#1D4ED8] bg-[#DBEAFE]',
    iconWrapperClassName: 'bg-[#1D4ED8]',
    textClassName: 'text-[#1D4ED8]',
  },
  info: {
    containerClassName: 'border border-[#75A4FA] bg-[#DAECFA]',
    iconWrapperClassName: 'bg-[#1748BA]',
    textClassName: 'text-[#061F63]',
  },
  warning: {
    containerClassName: 'border border-[#FAC775] bg-[#FAEEDA]',
    iconWrapperClassName: 'bg-[#BA7517]',
    textClassName: 'text-[#633806]',
  },
  countdown: {
    containerClassName: 'border border-[rgba(231,176,8,0.30)] bg-[rgba(231,176,8,0.10)]',
    iconClassName: 'text-[#E7B008]',
    textClassName: 'text-[#0F1729]',
  },
  danger: {
    containerClassName: 'border border-[#FECACA] bg-[#FEF2F2]',
    iconWrapperClassName: 'bg-[#B91C1C]',
    textClassName: 'text-[#B91C1C]',
  },
  neutral: {
    containerClassName: 'border border-[#D1D5DB] bg-[#F8FAFC]',
    iconWrapperClassName: 'bg-[#64748B]',
    textClassName: 'text-[#475569]',
  },
  confidential: {
    containerClassName: 'border border-[#F3F4F6] bg-[#F3F4F6] border-left',
    iconWrapperClassName: 'bg-[#64748B]',
    textClassName: 'text-[#475569]',
  },
  credential: {
    containerClassName: 'border-l-[4px] border-l-[#E8172C] bg-[#F3F4F6]',
    contentClassName: 'gap-3 sm:items-center',
    iconClassName: 'text-[#E8172C]',
    showIconCircle: false,
    textClassName: 'text-[#475569]',
  },
}

const parseCountdownToSeconds = (value) => {
  const parts = String(value || '')
    .trim()
    .split(':')

  if (parts.length !== 3) return null

  const [hours, minutes, seconds] = parts.map((part) => Number(part))

  if (
    [hours, minutes, seconds].some((part) => !Number.isFinite(part) || part < 0)
  ) {
    return null
  }

  return hours * 60 * 60 + minutes * 60 + seconds
}

const getSecondsUntil = (value) => {
  if (!value) return null

  const normalizedValue = String(value).includes('T')
    ? value
    : `${value}T00:00:00`
  const targetDate = new Date(normalizedValue)

  if (Number.isNaN(targetDate.getTime())) return null

  return Math.max(0, Math.floor((targetDate.getTime() - Date.now()) / 1000))
}

const formatCountdownSeconds = (value) => {
  const safeValue = Math.max(0, Number(value) || 0)
  const hours = Math.floor(safeValue / 3600)
  const minutes = Math.floor((safeValue % 3600) / 60)
  const seconds = safeValue % 60

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0',
  )}:${String(seconds).padStart(2, '0')}`
}

const resolveCountdownSeconds = ({ targetDate, initialValue }) => {
  const parsedInitialValue = parseCountdownToSeconds(initialValue)

  if (parsedInitialValue != null) {
    return parsedInitialValue
  }

  if (initialValue != null && Number.isFinite(Number(initialValue))) {
    return Math.max(0, Number(initialValue))
  }

  return getSecondsUntil(targetDate)
}

const ApplicationNotice = ({
  icon,
  tone = 'info',
  title = '',
  children,
  variant = '',
  countdownLabel = '',
  countdownTargetDate = '',
  countdownInitialValue = null,
  countdownFallback = '-',
  countdownExpiryPrefix = 'Expires',
  countdownExpiryText = '',
  countdownTextClassName = 'tabular-nums font-medium text-[#EA2427]',
  action = null,
  className = '',
  paddingClassName = 'px-4 py-4',
  contentClassName = '',
  iconWrapperClassName = '',
  textClassName = '',
  actionClassName = 'mt-3',
  showIconCircle,
}) => {
  const isCountdown = variant === 'countdown'
  const [countdownSeconds, setCountdownSeconds] = useState(() =>
    resolveCountdownSeconds({
      targetDate: countdownTargetDate,
      initialValue: countdownInitialValue,
    }),
  )
  const currentTone = isCountdown ? 'countdown' : tone
  const currentIcon = isCountdown
    ? icon || (
        <AlertTriangle
          className='h-[16px] w-[16px] text-[#E7B008]'
          strokeWidth={2}
        />
      )
    : icon
  const toneStyles = NOTICE_TONE_STYLES[currentTone] || NOTICE_TONE_STYLES.info
  const useIconCircle = isCountdown
    ? false
    : (showIconCircle ?? toneStyles.showIconCircle ?? true)
  const iconBaseClassName = useIconCircle
    ? 'mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-white sm:mt-0'
    : 'mt-0.5 flex flex-shrink-0 items-center justify-center sm:mt-0'
  const iconToneClassName = useIconCircle
    ? toneStyles.iconWrapperClassName
    : toneStyles.iconClassName || toneStyles.textClassName
  const countdownText =
    countdownSeconds == null
      ? countdownFallback
      : formatCountdownSeconds(countdownSeconds)
  const renderedChildren = isCountdown ? (
    <div className='flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between'>
      <div
        className='min-w-0 text-[14px] font-medium leading-5'
        style={{ color: '#0F1729' }}
      >
        {countdownLabel}{' '}
        <strong className={countdownTextClassName}>{countdownText}</strong>
      </div>

      {countdownExpiryText ? (
        <div
          className='text-[12px] font-normal leading-4'
          style={{ color: '#6B7280' }}
        >
          {countdownExpiryPrefix}: {countdownExpiryText}
        </div>
      ) : null}
    </div>
  ) : (
    children
  )

  useEffect(() => {
    if (!isCountdown) {
      return undefined
    }

    setCountdownSeconds(
      resolveCountdownSeconds({
        targetDate: countdownTargetDate,
        initialValue: countdownInitialValue,
      }),
    )

    if (!countdownTargetDate && !countdownInitialValue) {
      return undefined
    }

    const intervalId = window.setInterval(() => {
      if (parseCountdownToSeconds(countdownInitialValue) != null) {
        setCountdownSeconds((previousSeconds) => {
          if (previousSeconds == null || previousSeconds <= 1) return 0

          return previousSeconds - 1
        })
      } else {
        setCountdownSeconds(getSecondsUntil(countdownTargetDate))
      }
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [isCountdown, countdownTargetDate, countdownInitialValue])

  return (
    <div
      className={`rounded-[12px] ${paddingClassName} ${toneStyles.containerClassName} ${className}`}
    >
      <div
        className={`flex items-start ${toneStyles.contentClassName || 'gap-3 sm:items-center'} ${contentClassName}`}
      >
        {currentIcon ? (
          <div
            className={`${iconBaseClassName} ${iconToneClassName} ${iconWrapperClassName}`}
          >
            {currentIcon}
          </div>
        ) : null}

        <div className='min-w-0 flex-1'>
          <div
            className={`text-[13px] font-normal leading-5 [&_p]:!text-inherit [&_span]:!text-inherit ${toneStyles.textClassName} ${textClassName}`}
          >
            {title ? <p className='mb-1 font-semibold'>{title}</p> : null}
            {renderedChildren}
          </div>

          {action ? <div className={actionClassName}>{action}</div> : null}
        </div>
      </div>
    </div>
  )
}

export default ApplicationNotice
